import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Snackbar, Alert } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ModalPopup from "../../components/ui/ModalPopup";
import OtpInputForm from "../common-features/OtpInputForm";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import applicantDetailsServices from "../../services/api/service/applicantDetailsService";
import applicationDetailsServices from "../../services/api/service/applicationDetails";
import losServices from "../../services/api/service/losService";
import postsServices from "../../services/api/service/postsService";
import ConsentAuthorizationButton from "../../components/common/ConsentAuthorizationButton";
import { encryptData } from "../../utils/crypto";
import { getSanitizedPublicKeyFromResponse } from "../../utils/piiKey";
import { updateFormData } from "../../services/redux/slices/stepperDynamicFormSlice";
import greenCheckVerified from "../../assets/svgIcons/green-check-verified.svg";
import arrowRightVerify from "../../assets/svgIcons/arrow-right-verify.svg";
import type { RootState } from "../../services/redux/store";
import { maskValue } from "../../utils/helpers";
// import { setAdditionalSummaryConsentComplete } from "../../services/redux/slices/tabsSlice";

interface ConsentDetailsProps {
  formData: any;
  mainApplicantDetails?: any;
  coApplicantDetails?: any;
  trackingId?: string;
  onContinue?: () => void;
}

type ApplicantConsentRow = {
  key: string;
  applicantType: string;
  fullName: string;
  email: string;
  mobile: string;
  emailVerified: boolean;
  mobileVerified: boolean;
  panVerified: boolean;
  panValue: string;
  applicantId?: string | number;
};

const parseBool = (v: any) => v === true || v === "true";

const ConsentDetails: React.FC<ConsentDetailsProps> = ({
  formData,
  mainApplicantDetails,
  coApplicantDetails,
  trackingId,
  onContinue,
}) => {
  const dispatch = useDispatch();
  const customerConsentTime = useSelector(
    (state: RootState) => state.stepper.customerConsentTime,
  );
  const isPartnerJourney = useSelector(
    (state: RootState) => !!state.stepper.fetchUserApplicationData?.partnerStartedJourney,
  );
  const horizontalSteps = useSelector((state: RootState) => state.stepper.horizontalSteps || []);
  const templateId = useSelector((state: RootState) => state.stepper.templateId || "");
  const fetchUserApplicationData = useSelector(
    (state: RootState) => state.stepper.fetchUserApplicationData,
  );
  const applicationStatus = useSelector((state: RootState) => state.tabs.applicationStatus);
  const isSubmittedApplication = [
    "application submitted",
    "submitted",
  ].includes(String(fetchUserApplicationData?.status || applicationStatus || "").toLowerCase());

  const rows = useMemo<ApplicantConsentRow[]>(() => {
    const list: ApplicantConsentRow[] = [];

    const appFirstName =
      mainApplicantDetails?.firstName ||
      mainApplicantDetails?.studentDetailsFirstName ||
      formData?.firstName ||
      formData?.studentDetailsFirstName ||
      "Applicant";
    const appLastName =
      mainApplicantDetails?.lastName ||
      mainApplicantDetails?.studentDetailsLastName ||
      formData?.lastName ||
      formData?.studentDetailsLastName ||
      "";

    list.push({
      key: "applicant",
      applicantType: "Applicant",
      fullName: `${appFirstName} ${appLastName}`.trim(),
      email: String(mainApplicantDetails?.email || formData?.email || ""),
      mobile: String(
        mainApplicantDetails?.phone_number ||
          formData?.phone_number ||
          formData?.studentDetailsPhone_number ||
          "",
      ),
      emailVerified: parseBool(formData?.isEmailVerified),
      mobileVerified: parseBool(formData?.isMobileVerified),
      panVerified: parseBool(formData?.isPanVerified),
      panValue: String(formData?.pan ?? formData?.panNumber ?? ""),
    });

    const coKeys = Object.keys(formData || {}).filter((k) =>
      /^basicCoApplicant\d+Email$/.test(k),
    );

    const coKeysFromDetails = Object.keys(coApplicantDetails || {})
      .filter((k) => /^coapplicant\d+$/i.test(k))
      .map((k) => {
        const match = k.match(/coapplicant(\d+)/i);
        return match ? Number(match[1]) : NaN;
      })
      .filter((n) => !Number.isNaN(n));

    const coNumbers = (isPartnerJourney
      ? coKeysFromDetails
      : coKeys
          .map((k) => Number((k.match(/^basicCoApplicant(\d+)Email$/) || [])[1]))
          .concat(coKeysFromDetails)
    )
      .filter((n, idx, arr) => arr.indexOf(n) === idx)
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);

    coNumbers.forEach((n, index) => {
      const displayNumber = index + 1;
      const coObj = coApplicantDetails?.[`coapplicant${n}`] || {};
      const firstName =
        coObj?.coApplicantName ||
        coObj?.FirstName ||
        formData?.[`basicCoApplicant${n}FirstName`] ||
        `Co-Applicant ${displayNumber}`;
      const lastName =
        coObj?.coApplicantName
          ? ""
          : coObj?.LastName || formData?.[`basicCoApplicant${n}LastName`] || "";

      list.push({
        key: `coapplicant${n}`,
        applicantType: `Co-Applicant ${displayNumber}`,
        fullName: `${firstName} ${lastName}`.trim(),
        email: String(
          coObj?.coApplicantEmail ||
            coObj?.Email ||
            formData?.[`basicCoApplicant${n}Email`] ||
            "",
        ),
        mobile: String(
          coObj?.coApplicantPhone ||
            coObj?.PhoneNumber ||
            formData?.[`basicCoApplicant${n}PhoneNumber`] ||
            "",
        ),
        applicantId: coObj?.coApplicantId,
        emailVerified: parseBool(
          formData?.[`basicCoApplicant${n}IsEmailVerified`] ??
            coObj?.IsEmailVerified ??
            coObj?.isEmailVerified,
        ),
        mobileVerified: parseBool(
          formData?.[`basicCoApplicant${n}IsMobileVerified`] ??
            coObj?.IsMobileVerified ??
            coObj?.isMobileVerified,
        ),
        panVerified: parseBool(
          formData?.[`basicCoApplicant${n}IsPanVerified`] ??
            coObj?.IsPanVerified ?? coObj?.isPanVerified,
        ),
        panValue: String(
          formData?.[`basicCoApplicant${n}Pan`] ??
            formData?.[`basicCoApplicant${n}PanNumber`] ??
            coObj?.pan ?? coObj?.panNumber ?? "",
        ),
      });
    });

    return list;
  }, [formData, mainApplicantDetails, coApplicantDetails, isPartnerJourney]);

  const [emailVerifiedMap, setEmailVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.emailVerified])),
  );
  const [mobileVerifiedMap, setMobileVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.mobileVerified])),
  );
  const [panVerifiedMap, setPanVerifiedMap] = useState<Record<string, boolean>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.panVerified])),
  );
  const [panValueMap, setPanValueMap] = useState<Record<string, string>>(
    () => Object.fromEntries(rows.map((r) => [r.key, r.panValue])),
  );
  const [consentLinkLoading, setConsentLinkLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setEmailVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.emailVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });

    setMobileVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.mobileVerified]));
      Object.keys(prev).forEach((key) => {
        if (prev[key]) next[key] = true;
      });
      return next;
    });
    setPanVerifiedMap((prev) => {
      const next = Object.fromEntries(rows.map((r) => [r.key, r.panVerified]));
      Object.keys(prev).forEach((key) => { if (prev[key]) next[key] = true; });
      return next;
    });
    setPanValueMap((prev) => ({
      ...Object.fromEntries(rows.map((r) => [r.key, r.panValue])),
      ...prev,
    }));
  }, [rows]);

  useEffect(() => {
    if (!isPartnerJourney || !trackingId) return;
    const coApplicantRows = rows.filter(
      (row) => row.key !== "applicant" && row.applicantId
    );
    if (coApplicantRows.length === 0) return;

    coApplicantRows.forEach(async (row) => {
      try {
        const res: any =
          await applicationDetailsServices.getCoApplicantBasicDetails(
            String(row.applicantId),
            trackingId
          );
        const fields = Array.isArray(res?.data?.data) ? res.data.data : [];
        const getFlag = (pattern: RegExp) =>
          parseBool(
            fields.find((field: any) =>
              pattern.test(String(field?.name || field?.key || ""))
            )?.value
          );
        const emailVerified = getFlag(/IsEmailVerified$/i);
        const mobileVerified = getFlag(/IsMobileVerified$/i);

        setEmailVerifiedMap((prev) => ({
          ...prev,
          [row.key]: emailVerified || prev[row.key] || false,
        }));
        setMobileVerifiedMap((prev) => ({
          ...prev,
          [row.key]: mobileVerified || prev[row.key] || false,
        }));
      } catch (error) {
        console.error("Failed to fetch co-applicant basic verification", error);
      }
    });
  }, [isPartnerJourney, rows, trackingId]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });

  const [otpModal, setOtpModal] = useState<{
    open: boolean;
    type: "email" | "mobile";
    rowKey: string;
    value: string;
    key: string;
    otpTime: string;
  }>({
    open: false,
    type: "email",
    rowKey: "",
    value: "",
    key: "",
    otpTime: "",
  });

  const hasConsentForRow = (rowKey: string) => {
    if (rowKey === "applicant") {
      return !!customerConsentTime;
    }

    const n = Number((rowKey.match(/coapplicant(\d+)/i) || [])[1]);
    if (!n) return false;

    const coObj = coApplicantDetails?.[`coapplicant${n}`] || {};
    return Boolean(
      coObj?.coApplicantConsentTime ||
        coObj?.consentTime ||
        coObj?.customerConsentTime ||
        formData?.[`basicCoApplicant${n}ConsentTime`] ||
        formData?.[`basicCoApplicant${n}CustomerConsentTime`],
    );
  };

  const handleRequestOtp = async (
    row: ApplicantConsentRow,
    type: "email" | "mobile",
  ) => {
    try {
      const payload: any = {
        name: row.fullName || row.applicantType,
      };
      if (type === "email") payload.email = row.email?.toLowerCase();
      else payload.mobileNumber = row.mobile;

      const res: any = await eligibilityCheckServices.sendOtp(payload);
      if (res?.data?.status === "fail") {
        setSnackbar({ open: true, severity: "error", message: res?.data?.message || "Failed to send OTP" });
        return;
      }

      setOtpModal({
        open: true,
        type,
        rowKey: row.key,
        value: type === "email" ? row.email : row.mobile,
        key: res?.data?.data?.value || "",
        otpTime: res?.data?.data?.otpTime || "",
      });
    } catch {
      setSnackbar({ open: true, severity: "error", message: "Unable to send OTP" });
    }
  };

//   const handleVerifyOtp = async (otp: string) => {
//     try {
//       const row = rows.find((r) => r.key === otpModal.rowKey);
//       if (!row) return;

//       const userType = row.applicantType;
//       const payload: any = {
//         type: otpModal.type,
//         key: otpModal.key,
//         userType,
//       };
//       if (trackingId) payload.trackingId = trackingId;

//       if (otpModal.type === "email") {
//         payload.email = otpModal.value;
//         payload.emailOtp = otp;
//         payload.emailOtpCrtdAt = otpModal.otpTime;
//       } else {
//         payload.mobileNumber = otpModal.value;
//         payload.mobileOtp = otp;
//         payload.mobileOtpCrtdAt = otpModal.otpTime;
//       }

//       const res: any = await eligibilityCheckServices.verifyOtp(payload);
//       if (res?.data?.status !== "success") {
//         setSnackbar({ open: true, severity: "error", message: res?.data?.message || "OTP verification failed" });
//         return;
//       }

//       if (otpModal.type === "email") {
//         setEmailVerifiedMap((prev) => ({ ...prev, [otpModal.rowKey]: true }));
//       } else {
//         setMobileVerifiedMap((prev) => ({ ...prev, [otpModal.rowKey]: true }));
//       }

//       // For co-applicants, update verification status via API (same as ApplicantTabs)
//       const coMatch = otpModal.rowKey.match(/coapplicant(\d+)/i);
//       if (coMatch) {
//         const n = coMatch[1];
//         const fieldKey =
//           otpModal.type === "email"
//             ? `basicCoApplicant${n}IsEmailVerified`
//             : `basicCoApplicant${n}IsMobileVerified`;
//         try {
//           await applicantDetailsServices.updateCoApplicantFormData({
//             trackingId,
//             coApplicantType: `Co-Applicant ${n}`,
//             key: fieldKey,
//             value: true,
//           });
//         } catch (err) {
//           console.error("Failed to update co-applicant form data:", err);
//         }
//       }

//       setOtpModal((prev) => ({ ...prev, open: false }));
//       setSnackbar({ open: true, severity: "success", message: "Verified successfully" });
//     } catch {
//       setSnackbar({ open: true, severity: "error", message: "OTP verification failed" });
//     }
//   };

  const allVerified = rows.every(
    (r) => emailVerifiedMap[r.key] && mobileVerifiedMap[r.key],
  );

  useEffect(() => {
    // dispatch(setAdditionalSummaryConsentComplete(allVerified));
  }, [allVerified, dispatch]);

  const handleVerifyPan = async (row: ApplicantConsentRow) => {
    const panValue = String(panValueMap[row.key] || "").trim().toUpperCase();
    if (!panValue || panValue.toLowerCase() === "no") {
      setSnackbar({ open: true, severity: "error", message: "PAN is not available for verification." });
      return;
    }
    const coNumber = row.key.match(/coapplicant(\d+)/i)?.[1];
    const apiApplicantType = coNumber ? `Co-Applicant ${coNumber}` : "Applicant";
    const dob = row.key === "applicant" ? formData?.dob : formData?.[`basicCoApplicant${coNumber}dob`];
    if (!dob) {
      setSnackbar({ open: true, severity: "error", message: "Please provide Date of Birth before PAN verification." });
      return;
    }
    try {
      const piiResponse: any = await losServices.getPiiKey();
      const publicKey = getSanitizedPublicKeyFromResponse(piiResponse?.data?.p_k);
      if (!publicKey) throw new Error("Unable to load PAN verification key.");
      const response: any = await applicantDetailsServices.verifyPan(await encryptData({
        panNumber: panValue,
        fullName: row.fullName,
        dob: new Date(dob).toLocaleDateString("en-GB"),
        trackingId,
        applicantType: apiApplicantType,
      }, ["panNumber"], publicKey));
      if (response?.status !== 200 || String(response?.data?.data?.dateOfBirth || "").toLowerCase() === "invalid") {
        throw new Error("DOB mismatch. Please ensure the Date of Birth matches the PAN records.");
      }
      const updatedFormData = row.key === "applicant"
        ? { ...formData, pan: panValue, isPanVerified: true }
        : { ...formData, [`basicCoApplicant${coNumber}Pan`]: panValue, [`basicCoApplicant${coNumber}IsPanVerified`]: true};
      dispatch(updateFormData(updatedFormData));
      const sectionFields = horizontalSteps
        .flatMap((step: any) => step?.verticalSteps || [])
        .filter((step: any) => row.key === "applicant"
          ? String(step?.id ?? step?.verticalStepId) === "4"
          : ["9", "11"].includes(String(step?.id ?? step?.verticalStepId)))
        .flatMap((step: any) => step?.fields || [])
        .map((field: any) => ({ id: field?.id ?? field?.formFieldId, name: field?.name || field?.fieldKey }))
        .filter((field: any) => field.id && field.name && (row.key === "applicant" || field.name.includes(`CoApplicant${coNumber}`)));
      const data = sectionFields
        .filter((field: any) => updatedFormData[field.name] !== undefined && updatedFormData[field.name] !== null)
        .map((field: any) => ({ formFieldId: field.id, fieldKey: field.name, fieldValue: String(updatedFormData[field.name]) }));

      if (row.key === "applicant") {
        await applicantDetailsServices.createApplicant({ data, trackingId, templateId, formName: "Applicant & Loan Details", mobileNumber: row.mobile, isAadhaarSeeded: false, isThirdparty: true });
      } else {
        await postsServices.addPost("api/v1/applicant/save-coapplicant", { trackingId, coApplicantType: apiApplicantType, mobileNumber: row.mobile, email: row.email?.toLowerCase(), formData: data, section: "basic", isAadhaarSeeded: false, isThirdparty: true });
      }
      setPanValueMap((prev) => ({ ...prev, [row.key]: panValue }));
      setPanVerifiedMap((prev) => ({ ...prev, [row.key]: true }));
      setSnackbar({ open: true, severity: "success", message: "PAN verified successfully." });
    } catch (error: any) {
      setSnackbar({ open: true, severity: "error", message: error?.response?.data?.message || error?.message || "PAN verification failed." });
    }
  };

  const handleTriggerConsentAuthorization = async (row: ApplicantConsentRow) => {
    if (!trackingId || isPartnerJourney) return;
    const coNumber = row.key.match(/coapplicant(\d+)/i)?.[1];
    const userType = coNumber ? `Co-Applicant ${coNumber}` : "Applicant";
    setConsentLinkLoading((prev) => ({ ...prev, [row.key]: true }));
    try {
      const response = await eligibilityCheckServices.sendVerificationLink({
        trackingId: String(trackingId),
        link: `${window.location.origin}/consent-authorization`,
        userType,
      });
      setSnackbar({
        open: true,
        severity: "success",
        message: response?.data?.message || "Verification link sent successfully",
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        severity: "error",
        message: error?.response?.data?.message || "Unable to send verification link",
      });
    } finally {
      setConsentLinkLoading((prev) => ({ ...prev, [row.key]: false }));
    }
  };

  const shouldShowConsentMessage = (() => {
    const rowKey = otpModal.rowKey;
    if (!rowKey) return false;

    const hasConsent = hasConsentForRow(rowKey);
    const isEmailDone = !!emailVerifiedMap[rowKey] || hasConsent;
    const isPhoneDone = !!mobileVerifiedMap[rowKey] || hasConsent;
    return !isEmailDone && !isPhoneDone;
  })(); 
  return (
    <Box sx={{
      padding:"15px",
    }}>
      {rows.map((row) => (
        <Box
          key={row.key}
          sx={{
            mb: 1.5,
            p: 1.2,
            borderRadius: "4px",
            backgroundColor: "#dbdade",
            border: "1px solid #dbdade",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography sx={{ fontSize: "11px", color: "#4b465c", fontWeight: 600}}>
              {row.applicantType}: <strong>{row.fullName}</strong>
            </Typography>
            {/* {!isPartnerJourney && (
              <ConsentAuthorizationButton
                type={row.key === "applicant" ? "applicant" : "coapplicant"}
                coApplicantIndex={row.key.match(/coapplicant(\d+)/i)?.[1]}
                disabled={emailVerifiedMap[row.key] && mobileVerifiedMap[row.key]}
                loading={consentLinkLoading[row.key]}
                onClick={() => handleTriggerConsentAuthorization(row)}
              />
            )} */}
          </Box>

          <Box sx={{ mt: 0.6, display: "flex", flexWrap: "wrap", gap: 2 }}>
            <Typography sx={{ fontSize: "10px", color: "#4b465c", width: "22%", display: "flex", alignItems: "center", gap: 0.5}}>
             <span>Email: <strong>{row.email || "-"}</strong></span>
              {emailVerifiedMap[row.key] ? (
                  <Button
                onClick={() => handleRequestOtp(row, "email")}
                disabled={emailVerifiedMap[row.key] || !row.email}
                sx={{
                  backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                  color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                  "&:hover": {
                    backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: emailVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    WebkitTextFillColor: emailVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    opacity: 1,
                  },
                  textTransform: "none",
                  fontSize: emailVerifiedMap[row.key] ? "12px" : "10px",
                  fontWeight: emailVerifiedMap[row.key] ? 500 : 700,
                  px: "8px",
                  ml: 0.6,
                  minWidth: "74px",
                  borderRadius: "4px",
                  gap: "6px",
                  height: "24px",
                }}
              >
                {emailVerifiedMap[row.key] ? (
                  <>
                    <img
                      src={greenCheckVerified}
                      alt="Verified"
                      style={{ height: "12px", objectFit: "contain" }}
                    />
                    <Typography variant="inherit" sx={{ fontSize: "11px" }}>
                      Verified
                    </Typography>
                  </>
                ) : (
                  <></>
                )}
              </Button>
              ): <>&nbsp; &nbsp; &nbsp; &nbsp;</>}
            
            </Typography>

            <Typography sx={{ fontSize: "10px", color: "#4b465c", display: "flex", alignItems: "center", justifyContent: "space-between"  }}>
             <span> Mobile: <strong>{row.mobile || "-"}</strong></span>
              {mobileVerifiedMap[row.key] ? ( <Button
                onClick={() => handleRequestOtp(row, "mobile")}
                disabled={mobileVerifiedMap[row.key] || !row.mobile}
                sx={{
                  backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                  color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                  "&:hover": {
                    backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: mobileVerifiedMap[row.key] ? "transparent" : "#FEC10D",
                    color: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    WebkitTextFillColor: mobileVerifiedMap[row.key] ? "#28C76F" : "#000000",
                    opacity: 1,
                  },
                  textTransform: "none",
                  fontSize: mobileVerifiedMap[row.key] ? "12px" : "10px",
                  fontWeight: mobileVerifiedMap[row.key] ? 500 : 700,
                  px: "8px",
                  ml: 0.6,
                  minWidth: "74px",
                  borderRadius: "4px",
                  gap: "6px",
                  height: "24px",
                }}
              >
                {mobileVerifiedMap[row.key] ? (
                  <>
                    <img
                      src={greenCheckVerified}
                      alt="Verified"
                      style={{ height: "12px", objectFit: "contain" }}
                    />
                    <Typography variant="inherit" sx={{ fontSize: "11px" }}>
                      Verified
                    </Typography>
                  </>
                ) : (
                  <>
                    
                  </>
                )}
              </Button>): <>&nbsp;</>}
             
            </Typography>
          
          </Box>
          <Box sx={{ mt: 0.6, display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.5, color: "#4b465c", fontSize: "10px" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>PAN:</Typography>
              {panValueMap[row.key]?.toLowerCase() === "no" || panValueMap[row.key]?.toLowerCase() === "" ? (
                <Typography sx={{ fontSize: "10px", color: "#777487" }}>
                  PAN is not available for verification
                </Typography>
              ) : panVerifiedMap[row.key] ? (
               <>
                <Typography sx={{ fontSize: "10px", fontWeight: 600 }}>  {maskValue(panValueMap[row.key])}</Typography>
                <Button disabled sx={{ color: "#28C76F", backgroundColor: "transparent", "&.Mui-disabled": { color: "#28C76F", opacity: 1 }, textTransform: "none", fontSize: "12px", px: "8px", minWidth: "74px", gap: "6px", height: "24px" }}>
                  <img src={greenCheckVerified} alt="Verified" style={{ height: "12px" }} />
                  <Typography variant="inherit" sx={{ fontSize: "11px" }}>Verified</Typography>
                </Button>
               </>
              ) : (
                <>
                  <input
                    value={panValueMap[row.key] || ""}
                    disabled
                    aria-label={`${row.applicantType} PAN`}
                    style={{ height: "24px", width: "180px", border: "1px solid #c9c9c9", borderRadius: "4px", padding: "0 8px", textTransform: "uppercase" }}
                  />
                  <Button
                     onClick={() => handleVerifyPan(row)}
                     disabled={!panValueMap[row.key] || (!row.emailVerified || !row.mobileVerified ) || isSubmittedApplication || isPartnerJourney}
                    sx={{ backgroundColor: "#FEC10D", color: "#000", "&:hover": { backgroundColor: "#FEC10D" }, "&.Mui-disabled": { backgroundColor: "#FEC10D", color: "#000", opacity: 0.55 }, textTransform: "none", fontSize: "10px", fontWeight: 700, px: "8px", minWidth: "74px", borderRadius: "4px", gap: "6px", height: "24px" }}
                  >
                    Verify
                    <img src={arrowRightVerify} alt="Verify" style={{ height: "12px" }} />
                  </Button>
                </>
              )}
            </Box>
            </Box>
        </Box>
      ))}
      {/* <ModalPopup
        isOpen={otpModal.open}
        onClose={() => setOtpModal((prev) => ({ ...prev, open: false }))}
        title=""
        isOtpModal={true}
      >
        <OtpInputForm
          emailMasked={otpModal.type === "email" ? otpModal.value : undefined}
          mobileMasked={otpModal.type === "mobile" ? otpModal.value : undefined}
          onOtpSubmit={handleVerifyOtp}
          onResendOtp={async () => {
            const row = rows.find((r) => r.key === otpModal.rowKey);
            if (!row) return;
            await handleRequestOtp(row, otpModal.type);
          }}
          errorMessage={""}
          setErrorMsg={() => {}}
          showConsentMessage={shouldShowConsentMessage}
        />
      </ModalPopup> */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConsentDetails;
